/**
 * Territory Canvas V1 frontend API client.
 *
 * Wallet-scoped private management state. Reuses the EXACT hardened authority
 * (SIWE session cookie + memory-only session-bound CSRF shared with
 * territory-identity-api, so a wallet switch clears it once + browser Origin).
 * SAVE is the only state-changing mutation; refs reconcile server-side only at
 * SAVE (Work Order §15/§41).
 */
import { API_BASE } from "./api";
import { clearCsrfToken, getCsrfToken } from "./territory-identity-api";
import type { CanvasDoc, CanvasAuthority, CanvasElement, CanvasStyle } from "../domain/canvas-model";

export { clearCsrfToken };

export class CanvasApiError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "CanvasApiError";
    this.status = status;
    this.code = code;
  }
}

interface ErrorEnvelope { code?: string; message?: string; detail?: { code?: string; message?: string } }

async function parse(resp: Response): Promise<unknown> {
  const text = await resp.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = null; }
  if (!resp.ok) {
    const env = (body ?? {}) as ErrorEnvelope;
    throw new CanvasApiError(resp.status, env.code ?? env.detail?.code ?? "CANVAS_ERROR",
      env.message ?? env.detail?.message ?? resp.statusText);
  }
  return body;
}

export async function loadCanvas(identityId: string, signal?: AbortSignal): Promise<CanvasDoc> {
  const resp = await fetch(`${API_BASE}/api/v1/canvas/${encodeURIComponent(identityId)}`, {
    credentials: "include", cache: "no-store", signal,
  });
  return ((await parse(resp)) as { canvas: CanvasDoc }).canvas;
}

export async function saveCanvas(
  identityId: string,
  elements: CanvasElement[],
  style: CanvasStyle,
  authority: CanvasAuthority,
  signal?: AbortSignal,
): Promise<CanvasDoc> {
  const body = JSON.stringify({ elements, style, authority });
  const doPut = async (csrf: string): Promise<Response> =>
    fetch(`${API_BASE}/api/v1/canvas/${encodeURIComponent(identityId)}`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json", "X-The-Grid-CSRF": csrf },
      body,
      signal,
    });
  let resp = await doPut(await getCsrfToken());
  if (resp.status === 403) {
    const peek = await resp.clone().json().catch(() => null);
    const code = peek?.code ?? peek?.detail?.code;
    if (code === "CSRF_INVALID" || code === "CSRF_REQUIRED") {
      clearCsrfToken();
      resp = await doPut(await getCsrfToken(true));
    }
  }
  return ((await parse(resp)) as { canvas: CanvasDoc }).canvas;
}
